import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

// Business, branch, and department options - replace with API calls
const businessOptions = [
  { id: 1, name: 'YES Technologies' },
  { id: 2, name: 'YES Retail Solutions' },
  { id: 3, name: 'YES Consulting' },
];

const branchOptions = [
  { id: 1, businessId: 1, name: 'Head Office' },
  { id: 2, businessId: 2, name: 'Mumbai Branch' },
  { id: 3, businessId: 3, name: 'Delhi Branch' },
];

const departments = [
  { id: 1, businessId: 1, branchId: 1, name: 'Human Resources' },
  { id: 2, businessId: 2, branchId: 2, name: 'Finance' },
  { id: 3, businessId: 3, branchId: 3, name: 'IT' },
];

const AddDesignation = ({ onDesignationAdded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    businessId: '',
    branchId: '',
    departmentId: '',
    designationName: '',
    description: '',
    status: 'active',
  });

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      businessId: '',
      branchId: '',
      departmentId: '',
      designationName: '',
      description: '',
      status: 'active',
    });
    setErrors({});
    setSuccessMessage('');
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.businessId) newErrors.businessId = 'Business is required';
    if (!formData.branchId) newErrors.branchId = 'Branch is required';
    if (!formData.departmentId) newErrors.departmentId = 'Department is required';
    if (!formData.designationName) newErrors.designationName = 'Designation name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle add designation
  const handleAddDesignation = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Simulate API call - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newDesignation = {
        ...formData,
        id: Date.now(), // Generate unique ID
        createdDate: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
      };

      // Call parent callback to update designation list
      if (onDesignationAdded) {
        onDesignationAdded(newDesignation);
      }

      setSuccessMessage('Designation added successfully!');
      setTimeout(() => {
        setIsOpen(false);
        resetForm();
      }, 1500);
    } catch (error) {
      setErrors({ submit: 'Failed to add designation. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle dialog open/close
  const handleOpen = () => {
    resetForm();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  // Filter branches by selected business
  const filteredBranches = branchOptions.filter(b =>
    b.businessId?.toString() === formData.businessId?.toString()
  );

  // Filter departments by selected business and branch
  const filteredDepartments = departments.filter(d =>
    d.businessId?.toString() === formData.businessId?.toString() &&
    d.branchId?.toString() === formData.branchId?.toString()
  );

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{ textTransform: 'none' }}
      >
        Add Designation
      </Button>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          Add New Designation
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Business/Company Name */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={Boolean(errors.businessId)}>
                <InputLabel>Business / Company *</InputLabel>
                <Select
                  value={formData.businessId}
                  onChange={(e) => {
                    handleInputChange('businessId', e.target.value);
                    handleInputChange('branchId', '');
                    handleInputChange('departmentId', '');
                  }}
                  label="Business / Company *"
                >
                  {businessOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id.toString()}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.businessId && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.businessId}
                  </Alert>
                )}
              </FormControl>
            </Grid>

            {/* Branch */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={Boolean(errors.branchId)} disabled={!formData.businessId}>
                <InputLabel>Branch *</InputLabel>
                <Select
                  value={formData.branchId}
                  onChange={(e) => {
                    handleInputChange('branchId', e.target.value);
                    handleInputChange('departmentId', '');
                  }}
                  label="Branch *"
                >
                  {filteredBranches.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id.toString()}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.branchId && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.branchId}
                  </Alert>
                )}
                {filteredBranches.length === 0 && formData.businessId && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No branches available for selected business
                  </Alert>
                )}
              </FormControl>
            </Grid>

            {/* Department */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={Boolean(errors.departmentId)} disabled={!formData.branchId}>
                <InputLabel>Department *</InputLabel>
                <Select
                  value={formData.departmentId}
                  onChange={(e) => handleInputChange('departmentId', e.target.value)}
                  label="Department *"
                >
                  {filteredDepartments.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id.toString()}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.departmentId && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.departmentId}
                  </Alert>
                )}
                {filteredDepartments.length === 0 && formData.branchId && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No departments available for selected branch
                  </Alert>
                )}
              </FormControl>
            </Grid>

            {/* Designation Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Designation Name *"
                value={formData.designationName}
                onChange={(e) => handleInputChange('designationName', e.target.value)}
                error={Boolean(errors.designationName)}
                helperText={errors.designationName}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddDesignation}
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading ? 'Adding...' : 'Add Designation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddDesignation;
